import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  const { language } = useLanguage();
  
  const getPath = (path) => {
    return language === 'en' ? `/en${path}` : path;
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 bg-black">
      <div className="container mx-auto max-w-4xl">
        {/* Back button */}
        <Link 
          to={getPath('/')}
          className="inline-flex items-center space-x-2 text-gray-400 hover:text-[#D6CE15] transition-colors mb-8"
        >
          <ArrowLeft size={20} />
          <span>{language === 'et' ? 'Tagasi avalehele' : 'Back to homepage'}</span>
        </Link>

        {/* Content */}
        <div className="prose prose-invert prose-green max-w-none">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Privaatsuspoliitika
          </h1>
          
          <p className="text-gray-400 mb-8">Uuendatud: 9. detsember 2025</p>

          <div className="bg-[#0a0a0a] border border-[#D6CE15]/20 rounded-xl p-8 mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <Shield className="text-[#D6CE15]" size={32} />
              <h2 className="text-2xl font-bold text-white mb-0">GDPR Compliance</h2>
            </div>
            <p className="text-gray-300 leading-relaxed">Käesolev Poliitika vastab Määruse (EL) 2016/679 (GDPR) ja Eesti Isikuandmete kaitse seaduse nõuetele. Teie andmed on kaitstud kõrgeimal Euroopa tasemel.</p>
          </div>

          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">1. Üldсätted</h2>
              <p className="mb-4">Käesolev Poliitika kirjeldab, kuidas 13Labs OÜ (edaspidi "Ettevõte") töötleb isikuandmeid vastavalt Määrusele (EL) 2016/679 (GDPR) ja Eesti Isikuandmete kaitse seadusele.</p>
              <p className="mb-2">Ettevõte tegutseb:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Andmete vastutava töötlejana</strong> (veebisaidi ja turunduse käitamisel)</li>
                <li><strong>Andmete volitatud töötlejana</strong> (teenuste osutamisel klientidele lepingute ja DPA alusel)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">2. Milliseid andmeid me töötleme</h2>
              
              <h3 className="text-xl font-semibold text-[#D6CE15] mb-3">Isikuandmed</h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Nimi, ametinimetus</li>
                <li>Kontaktandmed (e-post, telefon)</li>
                <li>Vormide ja kirjavahetuse andmed</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#D6CE15] mb-3">Ettevõtte andmed (B2B)</h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Ettevõtte nimi</li>
                <li>Registrikood</li>
                <li>Juriidiline aadress</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#D6CE15] mb-3">Tehniline info</h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>IP-aadress</li>
                <li>Küpsised ja identifikaatorid</li>
                <li>Analüütika andmed</li>
              </ul>

              <div className="bg-[#1a1a1a] border border-[#D6CE15]/10 rounded-lg p-4 mt-4">
                <p className="text-[#D6CE15] font-semibold">Tähtis:</p>
                <p>Me EI kogu delikaatseid isikuandmeid (rassiline päritolu, religioon, tervis)</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">3. Eesmärgid ja õiguslikud alused</h2>
              <p className="mb-4">Töötlemine toimub järgmistel eesmärkidel:</p>
              
              <div className="space-y-4">
                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-2">Lepingu täitmine</h4>
                  <p className="text-sm text-gray-400 mb-2">Art. 6(1)(b) GDPR</p>
                  <p>Kommunikatsioon, arveldamine, teenuste osutamine</p>
                </div>

                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-2">Turundus</h4>
                  <p className="text-sm text-gray-400 mb-2">Art. 6(1)(a) GDPR</p>
                  <p>Teie nõusolekul (saab igal ajal tagasi võtta)</p>
                </div>

                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-2">Analüütika ja veebisaidi täiustamine</h4>
                  <p className="text-sm text-gray-400 mb-2">Art. 6(1)(f) GDPR</p>
                  <p>Ettevõtte õigustatud huvi</p>
                </div>

                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-2">Õiguslikud kohustused</h4>
                  <p className="text-sm text-gray-400 mb-2">Art. 6(1)(c) GDPR</p>
                  <p>Maksu- ja raamatupidamisseadused</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">4. AI ja pilveteenused</h2>
              <p className="mb-4">Teenuste osutamiseks kasutab 13Labs OÜ kaasaegseid AI-platvorme ja pilveteenuseid:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>OpenAI (ChatGPT, GPT-4)</li>
                <li>Google Cloud AI</li>
                <li>Microsoft Azure</li>
                <li>AWS (Amazon Web Services)</li>
              </ul>
              <p className="mt-4">Andmete edastamisel väljaspool EL-i rakendatakse kaitset <strong>ELi standardsete lepingutingimuste (SCC)</strong> ja Euroopa Komisjoni heakskiidetud muude mehhanismide kaudu.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">5. Andmete edastamine ja salvestamine</h2>
              <p className="mb-4">Andmeid võib edastada ainult:</p>
              
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-white mb-1">13Labs OÜ töötajatele ja töövõtjatele</h4>
                  <p className="text-sm">Allkirjastatud konfidentsiaalsuslepingutega</p>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Hosting-, pilve- ja analüütikateenuste pakkujatele</h4>
                  <p className="text-sm">AWS, Google Cloud, Vercel jt (DPA-ga)</p>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Riigiasutustele</h4>
                  <p className="text-sm">Ainult seaduslike nõuete korral (kohus, maksuamet)</p>
                </div>
              </div>

              <div className="mt-6 bg-[#1a1a1a] border border-[#D6CE15]/10 rounded-lg p-4">
                <h4 className="font-semibold text-[#D6CE15] mb-2">Säilitamisperiood</h4>
                <p><strong>Kuni 5 aastat</strong> pärast lepingu lõppu, kui seadus ei nõua teisiti (nt maksu- või raamatupidamisseadused — 7 aastat).</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">6. Andmesubjektide õigused</h2>
              <p className="mb-4">Vastavalt GDPR-ile on teil järgmised õigused:</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Juurdepääsuõigus</h4>
                  <p className="text-sm">Saada oma isikuandmete koopia</p>
                </div>
                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Parandamiseõigus</h4>
                  <p className="text-sm">Parandada ebakorrektseid andmeid</p>
                </div>
                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Kustutamiseõigus</h4>
                  <p className="text-sm">"Õigus olla unustatud" (teatud tingimustel)</p>
                </div>
                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Piiramiseõigus</h4>
                  <p className="text-sm">Piirata oma andmete töötlemist</p>
                </div>
                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Andmeteülekandeõigus</h4>
                  <p className="text-sm">Saada andmed struktureeritud vormingus</p>
                </div>
                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Vastuväiteõigus</h4>
                  <p className="text-sm">Võtta tagasi nõusolek töötlemiseks</p>
                </div>
              </div>

              <div className="mt-6 bg-[#1a1a1a] border border-[#D6CE15]/20 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-white mb-3">Kuidas oma õigusi kasutada?</h4>
                <p className="mb-3">Saatke taotlus aadressile <a href="mailto:info@polyusplus.com" className="text-[#D6CE15] hover:text-[#A4A71E]">info@polyusplus.com</a>. Vastame 30 päeva jooksul.</p>
                <p>Teil on õigus esitada kaebus järelevalveasutusele: <strong>Andmekaitse Inspektsioon</strong> (Eesti andmekaitseorgan) — <a href="https://aki.ee" target="_blank" rel="noopener" className="text-[#D6CE15] hover:text-[#A4A71E]">aki.ee</a></p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. Turvalisus</h2>
              <p className="mb-4">Ettevõte rakendab kaasaegseid tehnilisi ja organisatsioonilisi meetmeid teie isikuandmete kaitsmiseks volitamata juurdepääsu, lekke, muutmise või hävitamise eest.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Krüpteerimine</h4>
                  <p className="text-sm">SSL/TLS andmeedastuseks</p>
                </div>
                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Juurdepääsu kontroll</h4>
                  <p className="text-sm">Mitmefaktoriline autentimine</p>
                </div>
                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Turvalised serverid</h4>
                  <p className="text-sm">AWS, Google Cloud DPA-ga</p>
                </div>
                <div className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Rikkumisest teavitamine</h4>
                  <p className="text-sm">Järelevalveasutust 72 tunni jooksul</p>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-[#D6CE15]/20">
            <h3 className="text-xl font-semibold text-white mb-4">Kontaktandmed</h3>
            <p className="text-gray-300 mb-2"><strong>13Labs OÜ</strong></p>
            <p className="text-gray-300 mb-2">Registrikood: 17350520</p>
            <p className="text-gray-300 mb-2">Aadress: Uuslinna tn 11, Lasnamäe linnaosa, 11415 Tallinn, Harju maakond</p>
            <p className="text-gray-300 mb-6">Email: <a href="mailto:info@polyusplus.com" className="text-[#D6CE15] hover:text-[#A4A71E]">info@polyusplus.com</a></p>
            <p className="text-gray-300 mb-2">Veebisait: <a href="https://13labs.eu" className="text-[#D6CE15] hover:text-[#A4A71E]">13labs.eu</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;